/**
 * TodoTaskItem - Task card with sticky note styling and square checkboxes.
 * Matches the event modal's Tasks section appearance.
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  type GestureResponderEvent,
  Pressable,
  StyleSheet,
  Text,
  type TextInput,
  View,
} from 'react-native';
import Animated, {
  interpolateColor,
  useAnimatedStyle,
  useSharedValue,
  withSequence,
  withTiming,
} from 'react-native-reanimated';
import * as Haptics from 'expo-haptics';
import { Ionicons } from '@expo/vector-icons';

import { TodoSubtaskRow } from './TodoSubtaskRow';
import { TodoAddSubtaskRow } from './TodoAddSubtaskRow';
import { TodoInlineEditor } from './TodoInlineEditor';
import { useTodoScroll } from './TodoScrollContext';
import { DEFAULT_TASK_NOTE_COLOR } from '@/components/shared/fields/StickyNoteColorPicker';
import { Spacing, BorderRadius } from '@/constants/spacing';
import { useColorScheme } from '@/hooks/use-color-scheme';
import { Colors } from '@/constants/theme';
import type { Task } from '@/types/tasks';

// Sticky note design constants for light backgrounds (pastels)
const STICKY_TEXT_COLOR_DARK = '#333333';
const STICKY_CHECKBOX_COLOR_DARK = '#666666';
// For dark mode white cards that become charcoal
const STICKY_TEXT_COLOR_LIGHT = '#FFFFFF';
const STICKY_CHECKBOX_COLOR_LIGHT = '#B0B0B0';

// Dark mode display equivalents for pastel task note colors
const DARK_MODE_CARD_COLORS: Record<string, string> = {
  '#FFFFFF': '', // white → uses palette.cardBackground (handled separately)
  '#FFF9C4': '#4A4535', // yellow → muted dark yellow
  '#FFCCBC': '#4A3D35', // peach → muted dark peach
  '#E1BEE7': '#3D3545', // lavender → muted dark lavender
  '#C8E6C9': '#354538', // mint → muted dark mint
  '#C5CAE9': '#353D4A', // periwinkle → muted dark periwinkle
};

/** Darkens a hex color by a percentage (0–1) */
function darkenColor(hex: string, amount: number): string {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  const dr = Math.round(r * (1 - amount));
  const dg = Math.round(g * (1 - amount));
  const db = Math.round(b * (1 - amount));
  return `#${dr.toString(16).padStart(2, '0')}${dg.toString(16).padStart(2, '0')}${db.toString(16).padStart(2, '0')}`;
}

export type ActionMenuAnchor = {
  x: number;
  y: number;
  cardTop: number;
  cardBottom: number;
};

type TodoTaskItemProps = {
  task: Task;
  onToggle: (taskId: string) => void;
  onActionPress: (task: Task, anchor: ActionMenuAnchor) => void;
  onToggleSubtask?: (taskId: string, subtaskId: string) => void;
  onUpdateTitle?: (taskId: string, newTitle: string) => void;
  onAddSubtask?: (taskId: string, title: string, options?: { afterSubtaskId?: string }) => Promise<string | null>;
  onUpdateSubtaskTitle?: (taskId: string, subtaskId: string, newTitle: string) => void;
  onDeleteSubtask?: (taskId: string, subtaskId: string) => void;
  isFocused?: boolean;
  onFocusChange?: (taskId: string | null) => void;
  highlight?: boolean;
};

export const ITEM_HEIGHT = 56;
/** Total height including vertical margin used by the sortable layout */
export const ITEM_TOTAL_HEIGHT = ITEM_HEIGHT + Spacing.xxs * 2;

export function TodoTaskItem({
  task,
  onToggle,
  onActionPress,
  onToggleSubtask,
  onUpdateTitle,
  onAddSubtask,
  onUpdateSubtaskTitle,
  onDeleteSubtask,
  isFocused = false,
  onFocusChange,
  highlight = false,
}: TodoTaskItemProps) {
  const colorScheme = useColorScheme();
  const palette = Colors[colorScheme ?? 'light'];
  const containerRef = useRef<View>(null);
  const { scrollToCard } = useTodoScroll();
  const wasFocusedRef = useRef(isFocused);

  // Handle pastel cards in dark mode - use muted dark equivalents
  const rawColor = task.color ?? DEFAULT_TASK_NOTE_COLOR;
  const isWhiteInDarkMode = colorScheme === 'dark' && rawColor === '#FFFFFF';
  const hasDarkModeEquivalent = colorScheme === 'dark' && rawColor in DARK_MODE_CARD_COLORS;

  // In dark mode, use dark equivalents for all pastel colors
  let activeColor: string;
  if (isWhiteInDarkMode) {
    activeColor = palette.cardBackground;
  } else if (hasDarkModeEquivalent && DARK_MODE_CARD_COLORS[rawColor]) {
    activeColor = DARK_MODE_CARD_COLORS[rawColor];
  } else {
    activeColor = rawColor;
  }

  const borderColor = colorScheme === 'dark' ? palette.border : darkenColor(activeColor, 0.15);

  // Use light text for dark backgrounds (all dark mode cards need light text)
  const isDarkModeCard = colorScheme === 'dark';
  const stickyTextColor = isDarkModeCard ? STICKY_TEXT_COLOR_LIGHT : STICKY_TEXT_COLOR_DARK;
  const stickyCheckboxColor = isDarkModeCard ? STICKY_CHECKBOX_COLOR_LIGHT : STICKY_CHECKBOX_COLOR_DARK;

  const handleToggle = useCallback(() => {
    Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Light);
    onToggle(task.id);
  }, [onToggle, task.id]);

  const handleAction = useCallback((e: GestureResponderEvent) => {
    const pageX = e.nativeEvent.pageX;
    const pageY = e.nativeEvent.pageY;

    containerRef.current?.measure((_x, _y, _width, height, _pageCardX, pageCardY) => {
      onActionPress(task, {
        x: pageX,
        y: pageY,
        cardTop: pageCardY,
        cardBottom: pageCardY + height,
      });
    });
  }, [onActionPress, task]);

  const handleSubtaskToggle = useCallback((subtaskId: string) => {
    onToggleSubtask?.(task.id, subtaskId);
  }, [onToggleSubtask, task.id]);

  const handleUpdateSubtaskTitle = useCallback((subtaskId: string, newTitle: string) => {
    onUpdateSubtaskTitle?.(task.id, subtaskId, newTitle);
  }, [onUpdateSubtaskTitle, task.id]);

  const handleDeleteSubtask = useCallback((subtaskId: string) => {
    onDeleteSubtask?.(task.id, subtaskId);
  }, [onDeleteSubtask, task.id]);

  const [forceEditingSubtaskId, setForceEditingSubtaskId] = useState<string | null>(null);

  // Imperative ref-map of subtask TextInputs so we can focus a freshly-chained
  // row directly. Relying on the row's own useEffect+autoFocus is unreliable
  // when chaining on top of another already-focused TextInput.
  const subtaskInputsRef = useRef<Map<string, TextInput | null>>(new Map());
  const registerSubtaskInput = useCallback((subtaskId: string, ref: TextInput | null) => {
    if (ref) subtaskInputsRef.current.set(subtaskId, ref);
    else subtaskInputsRef.current.delete(subtaskId);
  }, []);

  const handleAddSubtask = useCallback((title: string) => {
    onAddSubtask?.(task.id, title);
  }, [onAddSubtask, task.id]);

  const handleChainSubtaskAfter = useCallback(async (afterSubtaskId: string) => {
    if (!onAddSubtask) return;
    // Blur the source row BEFORE we mount the new one. The chained row's
    // autoFocus alone can't override an already-focused TextInput on RN, so
    // we proactively release focus from the old row first. The keyboard stays
    // up because we re-focus the new node a few frames later (well under the
    // ~100ms iOS keyboard-dismiss window).
    subtaskInputsRef.current.get(afterSubtaskId)?.blur();
    const newId = await onAddSubtask(task.id, '', { afterSubtaskId });
    if (!newId) return;
    setForceEditingSubtaskId(newId);
    // Focus the new row once its TextInput has mounted and registered itself
    // in the ref map. Retry across timing windows because middle-of-list
    // inserts cause surrounding rows to reflow and the ref may not be ready
    // on the first frame.
    const tryFocus = () => {
      const node = subtaskInputsRef.current.get(newId);
      if (!node) return false;
      node.focus();
      return true;
    };
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        if (tryFocus()) return;
        setTimeout(() => {
          if (tryFocus()) return;
          setTimeout(tryFocus, 80);
        }, 30);
      });
    });
  }, [onAddSubtask, task.id]);

  const handleClearForceEditing = useCallback(() => {
    setForceEditingSubtaskId(null);
  }, []);

  const handleTitleSubmit = useCallback((newTitle: string) => {
    onUpdateTitle?.(task.id, newTitle);
  }, [onUpdateTitle, task.id]);

  const editingCountRef = useRef(0);

  const handleFieldEditingChange = useCallback((isEditing: boolean) => {
    editingCountRef.current += isEditing ? 1 : -1;
    if (editingCountRef.current < 0) editingCountRef.current = 0;

    if (isEditing) {
      onFocusChange?.(task.id);
    } else {
      setTimeout(() => {
        if (editingCountRef.current === 0) {
          onFocusChange?.(null);
        }
      }, 150);
    }
  }, [onFocusChange, task.id]);

  // When the card first becomes focused (it grows to reveal "+ Add subtask"),
  // ensure the whole card stays visible above the floating QuickAddBar/keyboard.
  useEffect(() => {
    if (isFocused && !wasFocusedRef.current) {
      // Wait a frame so the new "+ Add subtask" row has been laid out.
      setTimeout(() => scrollToCard(containerRef), 50);
    }
    wasFocusedRef.current = isFocused;
  }, [isFocused, scrollToCard]);

  const pulse = useSharedValue(0);

  useEffect(() => {
    if (highlight) {
      pulse.value = withSequence(
        withTiming(1, { duration: 250 }),
        withTiming(0, { duration: 550 }),
      );
    }
  }, [highlight, pulse]);

  const pulseStyle = useAnimatedStyle(() => ({
    backgroundColor: interpolateColor(
      pulse.value,
      [0, 1],
      ['transparent', 'rgba(14,165,233,0.18)'],
    ),
  }));

  const hasSubtasks = task.subtasks && task.subtasks.length > 0;
  // Show add subtask button only when card is focused and task is incomplete
  const showAddSubtask = onAddSubtask && !task.completed && isFocused;
  const showSubtasksSection = hasSubtasks || showAddSubtask;

  return (
    <View
      ref={containerRef}
      style={[
        styles.container,
        {
          backgroundColor: activeColor,
          borderColor: borderColor,
        },
      ]}
    >
      <Animated.View
        pointerEvents="none"
        style={[StyleSheet.absoluteFillObject, { borderRadius: BorderRadius.md }, pulseStyle]}
      />
      {/* Header row */}
      <View style={[styles.headerRow, showSubtasksSection && styles.headerRowWithSubtasks]}>
        <Pressable onPress={handleToggle} hitSlop={8}>
          <View
            style={[
              styles.checkboxSquare,
              { borderColor: stickyCheckboxColor },
              task.completed && [styles.checkboxSquareCompleted, { backgroundColor: stickyCheckboxColor }],
            ]}
          >
            {task.completed && (
              <Ionicons name="checkmark" size={16} color={isDarkModeCard ? activeColor : '#FFFFFF'} />
            )}
          </View>
        </Pressable>

        <View style={styles.titleContainer}>
          {onUpdateTitle ? (
            <TodoInlineEditor
              value={task.title}
              onSubmit={handleTitleSubmit}
              onEditingChange={handleFieldEditingChange}
              style={[
                styles.title,
                { color: stickyTextColor },
                task.completed && styles.titleCompleted,
              ]}
              palette={palette}
            />
          ) : (
            <Text
              style={[
                styles.title,
                { color: stickyTextColor },
                task.completed && styles.titleCompleted,
              ]}
              numberOfLines={2}
            >
              {task.title}
            </Text>
          )}
        </View>

        {task.priority === 'high' && (
          <Ionicons name="flag" size={14} color={palette.danger} />
        )}

        <Pressable onPress={handleAction} hitSlop={8} style={styles.actionButton}>
          <Ionicons name="ellipsis-horizontal" size={20} color={stickyCheckboxColor} />
        </Pressable>
      </View>

      {/* Subtasks section — each row handles its own focus */}
      {showSubtasksSection && (
        <View style={styles.subtasksContainer}>
          {task.subtasks?.map((subtask, index) => (
            <View key={subtask.id}>
              {index > 0 && <View style={styles.subtaskDivider} />}
              <TodoSubtaskRow
                subtask={subtask}
                palette={palette}
                onToggle={handleSubtaskToggle}
                onUpdateTitle={onUpdateSubtaskTitle ? handleUpdateSubtaskTitle : undefined}
                onDelete={isFocused && onDeleteSubtask ? handleDeleteSubtask : undefined}
                onEditingChange={handleFieldEditingChange}
                textColor={stickyTextColor}
                checkboxColor={stickyCheckboxColor}
                forceEditing={forceEditingSubtaskId === subtask.id}
                onForceEditingHandled={handleClearForceEditing}
                onSubmitChain={onAddSubtask ? () => handleChainSubtaskAfter(subtask.id) : undefined}
                onRegisterInput={registerSubtaskInput}
              />
            </View>
          ))}
          {showAddSubtask && (
            <>
              {hasSubtasks && <View style={styles.subtaskDivider} />}
              <TodoAddSubtaskRow onAddSubtask={handleAddSubtask} onEditingChange={handleFieldEditingChange} />
            </>
          )}
        </View>
      )}
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    borderWidth: 1,
    borderRadius: BorderRadius.md,
    padding: Spacing.lg,
    marginHorizontal: Spacing.lg,
    marginVertical: Spacing.xxs,
  },
  headerRow: {
    flexDirection: 'row',
    alignItems: 'center',
    minHeight: 24,
    gap: Spacing.md,
  },
  headerRowWithSubtasks: {
    paddingBottom: Spacing.md,
    borderBottomWidth: 1,
    borderBottomColor: 'rgba(0, 0, 0, 0.08)',
    marginBottom: Spacing.sm,
  },
  checkboxSquare: {
    width: 24,
    height: 24,
    borderRadius: BorderRadius.xs,
    borderWidth: 2,
    alignItems: 'center',
    justifyContent: 'center',
  },
  checkboxSquareCompleted: {
    // backgroundColor applied dynamically
  },
  titleContainer: {
    flex: 1,
  },
  title: {
    fontSize: 13,
    fontWeight: '700',
    lineHeight: 19,
    // color applied dynamically based on background
  },
  titleCompleted: {
    textDecorationLine: 'line-through',
    opacity: 0.5,
  },
  actionButton: {
    padding: Spacing.xs,
  },
  subtasksContainer: {
    paddingTop: Spacing.xs,
  },
  subtaskDivider: {
    height: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.08)',
    marginVertical: Spacing.xs,
  },
});
